import { addHours, format } from "date-fns";
import { updateTime } from "./date-utils";

// Format for 12-hour time display (e.g., "08:30 AM")
const TIME_FORMAT_12HR = "hh:mm a";

export enum DayFrequency {
  ONCE = "ONCE",
  TWICE = "TWICE",
  THRICE = "THRICE",
  FOUR_TIMES = "FOUR_TIMES",
}

/**
 * Calculates optimal dosage times based on the first dosage and frequency
 * @param firstDosage First dosage time string in format 'hh:mm a' (12-hour format with AM/PM)
 * @param dayFrequency Frequency of dosage per day
 * @returns Object containing calculated dosage times as strings in 'hh:mm a' format (12-hour format with AM/PM)
 */
export const calculateOptimalDosages = (
  firstDosage: string,
  dayFrequency: DayFrequency
): {
  secondDosage?: string;
  thirdDosage?: string;
  fourthDosage?: string;
} => {
  // Parse the time string to a Date object for manipulation
  // Using a base date (today) just for time calculations
  const baseDate = updateTime(new Date(), firstDosage);

  // Get the hour and minutes for calculations
  const firstHour = baseDate.getHours();
  const firstMinutes = baseDate.getMinutes();

  // Result object to store calculated times
  const result: {
    secondDosage?: string;
    thirdDosage?: string;
    fourthDosage?: string;
  } = {};

  switch (dayFrequency) {
    case DayFrequency.TWICE: {
      // For twice daily, aim for ~12 hours apart but adjust for waking hours
      // Try to space 12 hours apart
      const idealSecondTime = addHours(baseDate, 12);

      // If the second time would be between 10pm-7am, adjust it
      const secondHour = idealSecondTime.getHours();
      if (secondHour >= 22 || secondHour < 7) {
        // If first dose is before noon, set second dose to evening (8pm)
        // If first dose is after noon, set second dose to morning (8am)
        const adjustedTime = new Date(baseDate);
        adjustedTime.setHours(firstHour < 12 ? 20 : 8);
        adjustedTime.setMinutes(firstMinutes);
        result.secondDosage = format(adjustedTime, TIME_FORMAT_12HR);
      } else {
        result.secondDosage = format(idealSecondTime, TIME_FORMAT_12HR);
      }
      break;
    }

    case DayFrequency.THRICE: {
      // For three times daily, aim for morning, afternoon, evening
      // Get waking hours range (7am-10pm = 15 hours)

      // Determine which segment the first dose falls into
      let segment = 0;
      if (firstHour >= 7 && firstHour < 12) segment = 0; // morning
      else if (firstHour >= 12 && firstHour < 17) segment = 1; // afternoon
      else segment = 2; // evening

      // Create base times for morning (8am), afternoon (2pm), evening (8pm)
      const baseTimes = [8, 14, 20].map((hour) => {
        const time = new Date(baseDate);
        time.setHours(hour);
        time.setMinutes(firstMinutes);
        return time;
      });

      // Set the remaining dosages based on which segment the first dose is in
      result.secondDosage = format(
        baseTimes[(segment + 1) % 3],
        TIME_FORMAT_12HR
      );
      result.thirdDosage = format(
        baseTimes[(segment + 2) % 3],
        TIME_FORMAT_12HR
      );
      break;
    }

    case DayFrequency.FOUR_TIMES: {
      // For four times daily, aim for morning, noon, afternoon, evening
      // Create base times for 8am, 12pm, 4pm, 8pm
      const baseTimes = [8, 12, 16, 20].map((hour) => {
        const time = new Date(baseDate);
        time.setHours(hour);
        time.setMinutes(firstMinutes);
        return time;
      });

      // Determine which segment the first dose falls into
      let segment = 0;
      if (firstHour >= 7 && firstHour < 10) segment = 0; // morning
      else if (firstHour >= 10 && firstHour < 14) segment = 1; // noon
      else if (firstHour >= 14 && firstHour < 18) segment = 2; // afternoon
      else segment = 3; // evening

      // Set the remaining dosages based on which segment the first dose is in
      result.secondDosage = format(
        baseTimes[(segment + 1) % 4],
        TIME_FORMAT_12HR
      );
      result.thirdDosage = format(
        baseTimes[(segment + 2) % 4],
        TIME_FORMAT_12HR
      );
      result.fourthDosage = format(
        baseTimes[(segment + 3) % 4],
        TIME_FORMAT_12HR
      );
      break;
    }

    default:
      break;
  }

  return result;
};
